import React, { FunctionComponent, useEffect, useState, MouseEvent, useRef } from 'react';
import { connect } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { debounce } from 'debounce';
import { equals } from 'ramda';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IBindingCallback1 } from '../../../../common/models/callback';
import {
  fetchPositionRoutine,
  fetchPositionCountersRoutine,
  fetchCandidatesByPositionIdRoutine,
  changePositionNameRoutine, checkPositionNameRoutine
} from '../../routines';
import { IPositionProfile } from '../../../../common/models/position/IPositionProfile';
import RegularButton from '../../../../components/RegularButton';
import { FormattedMessage } from 'react-intl';
import Table from '../../../../components/Table';
import { CandidateStatus } from '../../../../common/enums/CandidateStatus';
import { IPositionProfileFilter } from '../../../../common/models/filter/IPositionProfileFilter';
import { ICandidatePositionProfile } from '../../../../common/models/candidate/ICandidatePositionProfile';
import { getColumns } from './utils';
import { ContactType } from '../../../../common/enums/ContactType';
import { ISelectedRole } from '../../../../common/models/contact/ISelectedRole';
import { showSurveyBuilderModalRoutine, showContactProfileModalRoutine } from '../../../../routines/history';
import CandidatesBulkActions from '../../../../containers/CandidatesBulkActions';
import { IRowAccessor } from '../../../../common/models/table/IRowAccessor';
import { IRequestAdditionalReferences } from '../../../../common/models/referenceRequest/IRequestAdditionalReferences';
import { requestAdditionalReferencesRoutine } from '../../../RequestBuilder/routines';
import Icon from '../../../../components/Icon';
import { CandidateFilterType } from '../../../../common/enums/CandidateFilterType';
import { CandidateCounts } from '../../../../common/models/candidate/CandidateCounts';
import FilterDropdown from '../FilterDropdown';
import AvgScore from '../../../../components/AvgScore';
import IconButton from '../../../../components/IconButton';
import styles from './styles.module.scss';
import { updateCandidateStatusRoutine } from '../../../../containers/CandidatesBulkActions/routines';
import { ICandidateUpdateStatus } from '../../../../common/models/candidate/ICandidateUpdateStatus';
import ArchiveCandidateModal from '../../../../containers/ArchiveCandidateModal';
import { setReportCandidatesRoutine } from '../../../../containers/ShareReportModal/routines';
import { handleEnter } from '../../../../common/helpers/keyboardHelper';
import { IPositionAvgCounters } from '../../../../common/models/position/IPositionAvgCounters';
import Illustration from '../../../../components/Illustration';
import { ITableRef } from '../../../../common/models/table/ITableRef';

interface IPositionProfileProps {
  positionId: string;
  positionName?: string;
  position: IPositionProfile;
  candidates: ICandidatePositionProfile[];
  total: number;
  loading: boolean;
  isNameTaken: boolean;
  filterCounters: CandidateCounts | undefined;
  isChecking: boolean;
  averageCounters: IPositionAvgCounters;
  isCreatingReport: boolean;
  isSendingReminder: boolean;
  isSavingRefReq: boolean;
  fetchCandidates: IBindingCallback1<IPositionProfileFilter>;
  showSurveyBuilderModal: IBindingCallback1<string>;
  fetchPosition: IBindingCallback1<string>;
  fetchPositionCounters: IBindingCallback1<string>;
  showContactProfileModal: IBindingCallback1<ISelectedRole>;
  requestAdditionalReferences: IBindingCallback1<IRequestAdditionalReferences>;
  changeName: IBindingCallback1<string>;
  updateCandidateStatus: IBindingCallback1<ICandidateUpdateStatus>;
  checkPositionName: IBindingCallback1<{ id: string; name: string }>;
  setReportCandidates: IBindingCallback1<string[]>;
}

const PositionProfile: FunctionComponent<IPositionProfileProps> = ({
  positionId,
  position,
  positionName,
  total,
  loading,
  candidates,
  filterCounters,
  isNameTaken,
  isChecking,
  averageCounters,
  isCreatingReport,
  isSendingReminder,
  isSavingRefReq,
  fetchPosition,
  fetchCandidates,
  fetchPositionCounters,
  showSurveyBuilderModal,
  showContactProfileModal,
  requestAdditionalReferences,
  updateCandidateStatus,
  changeName,
  checkPositionName,
  setReportCandidates
}) => {
  const defaultFilter: IPositionProfileFilter = {
    from: 0,
    to: 25,
    id: positionId,
    candidateStatusFilter: [CandidateFilterType.AllActive],
    sort: [{ id: 'referenceProgress', desc: true }],
    filter: []
  };
  const [selected, setSelected] = useState<ICandidatePositionProfile[]>([]);
  const [filter, setFilter] = useState(defaultFilter);
  const [isNameUpdating, setIsNameUpdating] = useState(false);
  const [archiveCandidateId, setArchiveCandidateId] = useState<string | undefined>();
  const [totalCount, setTotalCount] = useState(total);
  const ref = useRef<ITableRef>(null);

  useEffect(() => setTotalCount(total), [total]);

  useEffect(() => {
    fetchPosition(positionId);
  }, []);

  useEffect(() => {
    fetchCandidates(filter);
  }, [filter]);

  useEffect(() => {
    if (!filterCounters && positionId) {
      fetchPositionCounters(positionId);
      fetchCandidates(filter);
    }
  }, [filterCounters]);

  const updateFilter = (newFilter: Partial<IPositionProfileFilter>) => {
    const updatedFilter = { ...filter, ...newFilter };

    if (!equals(updatedFilter, filter)) {
      setFilter(updatedFilter);
    }
  };

  const updateStatusFilter = (candidateStatusFilter: CandidateFilterType[]) => {
    updateFilter({ ...filter, candidateStatusFilter });
  };

  const loadData = (from: number, to: number) => {
    fetchCandidates({ ...filter, from, to });
  };

  const onRequestAdditionalReferences = () => {
    requestAdditionalReferences({ selectedPosition: { id: position?.id, name: position?.name }, candidates: [] });
  };

  const search = debounce((v: string) => checkPositionName({ name: v.trim(), id: position.id }), 500);

  const onRunReport = (id: string) => setReportCandidates([id]);

  useEffect(() => {
    if (positionName && isNameUpdating) {
      search(positionName);
    }
    return () => search.clear();
  }, [positionName]);

  useEffect(() => {
    if (!isCreatingReport || !isSendingReminder || !isSavingRefReq) {
      ref.current?.deselectRows();
    }
  }, [isCreatingReport, isSendingReminder, isSavingRefReq]);

  const onCellClick = (
    { accessor, original }: IRowAccessor<ICandidatePositionProfile>,
    event: MouseEvent<HTMLElement>
  ) => {
    switch (accessor) {
      case 'name':
        showContactProfileModal({
          contactId: original.contactId,
          candidateId: original.id,
          role: ContactType.Candidate
        });
        break;
      case 'survey':
        showSurveyBuilderModal(original.surveyId);
        break;
      case 'owner':
      case 'actions':
        event.stopPropagation();
        break;
      default:
        break;
    }
  };

  const namePlaceholder = {
    id: 'surveyBuilder.namePlaceholder',
    defaultMessage: 'Untitled position'
  };

  const unarchiveCandidate = (id: string) => updateCandidateStatus({ ids: [id], status: CandidateStatus.Active });

  const PositionInfo = () => (
    <>
      <div className={`position-relative ${styles.positionInfo}`}>
        <div className="d-flex align-items-center">
          <Icon icon="position" className={styles.positionIcon} />
        </div>
        <div className="d-flex flex-column justify-content-center mt-1">
          {isChecking && (
            <Spinner className={styles.nameSpinner} animation="border" size="sm" />
          )}
          {isNameUpdating
            ? (
              <FormattedMessage {...namePlaceholder}>
                {placeholder => (
                  <input
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    placeholder={placeholder as string}
                    className={`${styles.positionTitle} avenir-font ${isNameTaken ? styles.inputError : ''}`}
                    value={positionName}
                    onChange={({ target }) => changeName(target.value)}
                    onBlur={() => setIsNameUpdating(false)}
                    onKeyDown={event => handleEnter(event, () => setIsNameUpdating(false))}
                  />
                )}
              </FormattedMessage>
            )
            : (
              <>
                <div className={`${styles.positionTitle} ${styles.infoItem} avenir-font`}>
                  <span
                    className={
                      `overflow-ellipsis ${positionName ? '' : styles.secondaryText}`
                    }
                  >
                    {positionName ? positionName.trim() : <FormattedMessage {...namePlaceholder} />}
                  </span>
                  <IconButton onClick={() => setIsNameUpdating(true)} icon={<Icon icon="pencil" />} />
                </div>
              </>
            )}
          {isNameTaken && (
            <div className="name-error">
              <Icon icon="warning" />
              <FormattedMessage id="titleTaken" defaultMessage="Title already in use" />
            </div>
          )}
        </div>
      </div>
    </>
  );

  const noDataPlaceholder = (
    <Illustration
      variant="binoculars"
      header={<FormattedMessage id="resultsNotFound" defaultMessage="Hmm... no matches found" />}
      description={
        <FormattedMessage id="positionProfile.noData" defaultMessage="No active candidates for this position" />
    }
      height="22rem"
    />
  );

  return (
    <>
      <div className="profile-modal">
        <ArchiveCandidateModal
          isShown={Boolean(archiveCandidateId)}
          onHide={() => setArchiveCandidateId(undefined)}
          candidates={archiveCandidateId ? [archiveCandidateId] : []}
        />
        <div className="d-flex flex-row align-items-center profile-header">
          {position && <PositionInfo />}
        </div>
        <div className="profile-body">
          <div className="d-flex justify-content-between profile-pane-header">
            <div>
              <FilterDropdown onChange={updateStatusFilter} disabled={Boolean(selected.length)} />
            </div>
            <div className={styles.btnWrp}>
              {selected.length > 0
                ? <CandidatesBulkActions candidates={selected} />
                : (
                  <div className="ml-3">
                    <RegularButton
                      variant="tertiary"
                      textIntlId="candidateHeader.newCandidate"
                      defaultMessage="New candidate"
                      icon={<Icon icon="plus" className="icon-plus" />}
                      onClick={onRequestAdditionalReferences}
                    />
                  </div>
                )}
            </div>
          </div>
          <div className="profile-pane-table">
            <Table
              ref={ref}
              config={{
                columns: getColumns(setArchiveCandidateId, unarchiveCandidate, onRunReport),
                onFilterUpdated: updateFilter,
                data: { items: candidates, total: totalCount },
                onLoad: loadData,
                loading,
                sortingEnabled: true,
                filteringEnabled: true,
                rowSelection: 'multiple',
                onSelectionChanged: setSelected,
                onCellClicked: onCellClick,
                defaultSorted: filter.sort,
                noDataPlaceholder
              }}
            />
          </div>
        </div>
        <div className="profile-pane-footer">
          <div className="d-flex justify-content-end">
            <AvgScore
              score={averageCounters.hired}
              intlId="positionProfile.hiredAvg"
              defaultMessage="Hired candidates avg."
            />
            <AvgScore
              score={averageCounters.notHired}
              intlId="positionProfile.hibernateAvg"
              defaultMessage="Not hired avg."
            />
            <AvgScore
              score={averageCounters.active}
              intlId="positionProfile.activeAvg"
              defaultMessage="Active candidates avg."
            />
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = ({
  positionProfile: {
    position,
    positionId,
    positionName,
    isNameTaken,
    candidates,
    isChecking,
    total,
    loading,
    filterCounters,
    averageCounters
  },
  report: { candidateIds },
  sendReminder: { isSending: isSendingReminder },
  requestBuilder: { isSaving }
}: IAppState) => ({
  position: position as IPositionProfile,
  positionId: positionId as string,
  positionName: positionName as string,
  candidates,
  total,
  loading,
  isChecking,
  isNameTaken,
  filterCounters,
  averageCounters,
  isCreatingReport: Boolean(candidateIds),
  isSendingReminder,
  isSavingRefReq: isSaving
});

const mapDispachToProps = {
  fetchPosition: fetchPositionRoutine,
  fetchPositionCounters: fetchPositionCountersRoutine,
  fetchCandidates: fetchCandidatesByPositionIdRoutine,
  showSurveyBuilderModal: showSurveyBuilderModalRoutine,
  showContactProfileModal: showContactProfileModalRoutine,
  requestAdditionalReferences: requestAdditionalReferencesRoutine,
  changeName: changePositionNameRoutine,
  checkPositionName: checkPositionNameRoutine,
  updateCandidateStatus: updateCandidateStatusRoutine,
  setReportCandidates: setReportCandidatesRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(PositionProfile);
